import { useRef, useEffect, useState } from "react"
import useTranslations from "hooks/useTranslations"
import Slider from "react-slick"

interface SituationCardProps {
  title: string
  description: string
}

const SituationCard = ({ title, description }: SituationCardProps) => {
  return (
    <div className="relative mb-6 h-full md:mb-0">
      <div className="flex h-full min-h-[400px] flex-col justify-between rounded-[40px] bg-kkm-green p-7 text-zinc-800">
        <div className="space-y-8">
          <div className="my-1 inline-block rounded-[24px] bg-zinc-800/75 px-8 py-3 text-center text-2xl font-medium text-white transition-opacity hover:opacity-90 lg:text-left">
            {title}
          </div>
          <p className="text-xl font-semibold leading-tight lg:text-2xl">
            {description}
          </p>
        </div>
      </div>
    </div>
  )
}

export default function SituationsCarousel() {
  const sliderRef = useRef<Slider>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [isVisible, setIsVisible] = useState(false)

  const {
    situationsTitle,
    situation1Title,
    situation1Description,
    situation2Title,
    situation2Description,
    situation3Title,
    situation3Description,
    situation4Title,
    situation4Description,
    situation5Title,
    situation5Description,
  } = useTranslations()

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting)
      },
      { threshold: 0.8 },
    )

    if (containerRef.current) {
      observer.observe(containerRef.current)
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (sliderRef.current) {
      if (isVisible) {
        sliderRef.current.slickPlay()
      } else {
        sliderRef.current.slickPause()
      }
    }
  }, [isVisible])

  const situations = [
    {
      title: situation3Title,
      description: situation3Description,
    },
    {
      title: situation1Title,
      description: situation1Description,
    },
    {
      title: situation2Title,
      description: situation2Description,
    },
    {
      title: situation4Title,
      description: situation4Description,
    },
    {
      title: situation5Title,
      description: situation5Description,
    },
  ]

  return (
    <div
      ref={containerRef}
      className="relative overflow-hidden bg-white bg-zinc-800 py-16 lg:py-24"
    >
      <div className="mx-auto max-w-7xl lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-stretch lg:gap-12">
          <div className="flex shrink-0 flex-col justify-between px-4 pb-8 lg:w-1/3 lg:px-0">
            <h2 className="text-5xl font-black leading-none tracking-tight text-gray-50 xl:text-6xl">
              {situationsTitle}
            </h2>
            <div className="mt-8 flex items-center gap-4 text-zinc-800">
              <button
                className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-kkm-green transition-colors hover:bg-kkm-lightgreen"
                onClick={() => sliderRef.current?.slickPrev()}
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <button
                className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-kkm-green transition-colors hover:bg-kkm-lightgreen"
                onClick={() => sliderRef.current?.slickNext()}
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          </div>

          <div className="relative min-w-0 flex-1 pl-4">
            <div className="pointer-events-none absolute right-0 top-0 z-10 h-full w-6 bg-gradient-to-r from-transparent  to-zinc-800 lg:w-12 " />
            <Slider
              ref={sliderRef}
              className="situations-slider"
              slidesToShow={1.03}
              slidesToScroll={1}
              infinite
              dots={false}
              arrows={false}
              autoplay
              autoplaySpeed={5000}
              speed={1000}
              cssEase="ease-in-out"
              centerMode={false}
              pauseOnHover
              swipeToSlide
            >
              {situations.map((situation, index) => (
                <div
                  key={`${situation.title}-${index}`}
                  className="h-full pl-4 lg:pl-6"
                >
                  <SituationCard
                    title={situation.title}
                    description={situation.description}
                  />
                </div>
              ))}
            </Slider>
          </div>
        </div>
      </div>
    </div>
  )
}
